import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, AlertTriangle, TrendingUp, FileSpreadsheet, Calendar, Shield, BarChart3, Eye, Zap, Coffee, StickyNote, Calculator, Monitor, Sparkles, Clock, DollarSign } from 'lucide-react';
import { getCTAClasses, CTA_MESSAGES } from '@/lib/cta-config';

export const BeforeAfterSection: React.FC = () => {
  const [isTransformed, setIsTransformed] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const navigate = useNavigate();

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);
  
  // Dashboard mockup data for visual representation
  const dashboardMetrics = [
    { label: 'Current Balance', value: '$24,847', status: 'safe', icon: DollarSign },
    { label: 'Next Payment', value: '$3,247', date: 'Feb 15', icon: Calendar },
    { label: 'Forecast Accuracy', value: '95.8%', trend: 'up', icon: TrendingUp },
  ];

  // Enhanced chart data for lottery payments
  const chartData = [
    { month: 'Jan', actual: 2800, projected: null, value: 2800 },
    { month: 'Feb', actual: null, projected: 3247, value: 3247 },
    { month: 'Mar', actual: null, projected: 2950, value: 2950 },
    { month: 'Apr', actual: null, projected: 3100, value: 3100 },
    { month: 'May', actual: null, projected: 2750, value: 2750 },
    { month: 'Jun', actual: null, projected: 3300, value: 3300 },
  ];


  // Spreadsheet chaos data
  const chaosElements = [
    { type: 'spreadsheet', title: 'Lottery_Payments_Jan.xlsx', position: { top: '20%', left: '15%' }, rotation: -8 },
    { type: 'spreadsheet', title: 'URGENT_Tuesday_Calc.xlsx', position: { top: '35%', left: '45%' }, rotation: 12 },
    { type: 'spreadsheet', title: 'Payment_Tracking_OLD.xlsx', position: { top: '55%', left: '25%' }, rotation: -15 },
    { type: 'calculator', position: { top: '70%', left: '60%' }, rotation: 5 },
    { type: 'sticky', text: 'Check payments!', position: { top: '25%', left: '70%' }, rotation: 22 },
    { type: 'sticky', text: '$3,247 due soon??', position: { top: '45%', left: '15%' }, rotation: -18 },
  ];

  return (
    <section className="py-20 bg-gradient-to-b from-slate-50 to-white overflow-hidden relative">
      {/* Section Background Effects */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Floating Spreadsheet Fragments that Transform */}
        {Array.from({ length: 15 }).map((_, i) => (
          <motion.div
            key={i}
            className="absolute opacity-5"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              x: [0, Math.random() * 100 - 50],
              y: [0, Math.random() * 100 - 50],
              rotate: [0, Math.random() * 360],
              scale: [1, Math.random() * 0.5 + 0.5],
              opacity: [0.05, 0.15, 0.05],
            }}
            transition={{
              duration: 10 + Math.random() * 10,
              repeat: Infinity,
              ease: "linear",
            }}
          >
            {Math.random() > 0.5 ? (
              <FileSpreadsheet className="w-12 h-12 text-red-400" />
            ) : (
              <BarChart3 className="w-12 h-12 text-emerald-400" />
            )}
          </motion.div>
        ))}

        {/* Data Flow Lines */}
        <svg className="absolute inset-0 w-full h-full">
          <defs>
            <linearGradient id="sectionFlowGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#ef4444" stopOpacity="0.2" />
              <stop offset="50%" stopColor="#06b6d4" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#10b981" stopOpacity="0.2" />
            </linearGradient>
          </defs>
          {Array.from({ length: 12 }).map((_, i) => (
            <motion.path
              key={i}
              d={`M ${Math.random() * 100} ${Math.random() * 100} Q ${Math.random() * 1200} ${Math.random() * 400} ${Math.random() * 1400} ${Math.random() * 800}`}
              stroke="url(#sectionFlowGradient)"
              strokeWidth="2"
              fill="none"
              strokeDasharray="10,5"
              initial={{ pathLength: 0, opacity: 0 }}
              animate={{ 
                pathLength: [0, 1, 0],
                opacity: [0, 0.4, 0],
              }}
              transition={{
                duration: 12,
                delay: i * 0.8,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
          ))}
        </svg>

        {/* Morphing Particles */}
        {Array.from({ length: 30 }).map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              scale: [0, 1, 0],
              opacity: [0, 0.6, 0],
              backgroundColor: [
                '#ef4444', // red (chaos)
                '#06b6d4', // cyan (transformation)
                '#10b981', // emerald (confidence)
              ],
            }}
            transition={{
              duration: 4,
              delay: Math.random() * 3,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        ))}

        {/* Pulsing Energy Rings */}
        {Array.from({ length: 6 }).map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full border-2 border-opacity-20"
            style={{
              left: `${15 + i * 15}%`,
              top: `${20 + (i % 2) * 60}%`,
              width: '120px',
              height: '120px',
            }}
            animate={{
              scale: [1, 1.5, 1],
              opacity: [0.2, 0.5, 0.2],
              borderColor: [
                'rgba(239, 68, 68, 0.3)',
                'rgba(6, 182, 212, 0.4)', 
                'rgba(16, 185, 129, 0.3)',
              ],
            }}
            transition={{
              duration: 6,
              delay: i * 0.8,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        ))}
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            Watch The{' '}
            <span className="bg-gradient-to-r from-emerald-600 via-cyan-600 to-blue-600 bg-clip-text text-transparent">
              Transformation
            </span>
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto mb-8">
            See your business transform from chaos to confidence in real-time.
          </p>
          <motion.p 
            className="text-emerald-600 font-medium cursor-pointer"
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            👆 Hover over the workspace below
          </motion.p>
        </motion.div>

        {/* Interactive Desktop Workspace */}
        <div className="relative max-w-5xl mx-auto">
          <motion.div
            className="relative bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl p-8 shadow-2xl"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            onHoverStart={() => setIsTransformed(true)}
            onHoverEnd={() => setIsTransformed(false)}
          >
            {/* Monitor Frame */}
            <div className="relative bg-white rounded-2xl overflow-hidden shadow-inner min-h-[600px]">
              
              {/* Desktop Taskbar */}
              <div className="bg-slate-100 px-4 py-2 flex items-center justify-between border-b">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                  <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                  <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                </div>
                <div className="text-xs text-slate-600 font-medium">
                  {isTransformed ? 'LotteryFlow Dashboard' : 'Mike\'s Convenience Store - Office'}
                </div>
                <div className="text-xs text-slate-500 font-mono">
                  {currentTime.toLocaleTimeString()}
                </div>
              </div>

              {/* Workspace Content */}
              <div className="relative h-[550px] bg-gradient-to-br from-slate-50 to-slate-100 p-6 overflow-hidden">
                
                {/* Store Owner Avatar */}
                <motion.div
                  className="absolute bottom-4 left-4 z-20"
                  animate={{
                    scale: isTransformed ? 1.1 : 1,
                  }}
                  transition={{ duration: 0.6 }}
                >
                  <div className="relative">
                    <div className="w-16 h-16 bg-gradient-to-br from-amber-100 to-amber-200 rounded-full border-4 border-white shadow-lg flex items-center justify-center">
                      <motion.span 
                        className="text-2xl"
                        animate={{
                          rotate: isTransformed ? [0, 10, -10, 0] : 0,
                        }}
                        transition={{ duration: 0.6 }}
                      >
                        {isTransformed ? '😊' : '😰'}
                      </motion.span>
                    </div>
                    <motion.div
                      className="absolute -top-2 -right-2"
                      animate={{
                        scale: isTransformed ? [1, 1.2, 1] : 0,
                        rotate: isTransformed ? [0, 360] : 0,
                      }}
                      transition={{ duration: 0.8, delay: 0.3 }}
                    >
                      <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                        <CheckCircle className="w-4 h-4 text-white" />
                      </div>
                    </motion.div>
                    <motion.div
                      className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 whitespace-nowrap text-xs font-medium px-2 py-1 rounded-full shadow-lg"
                      animate={{
                        backgroundColor: isTransformed ? '#10b981' : '#ef4444',
                        color: '#ffffff',
                      }}
                      transition={{ duration: 0.4 }}
                    >
                      {isTransformed ? 'Confident' : 'Stressed'}
                    </motion.div>
                  </div>
                </motion.div>

                {/* Chaos Elements (Before) */}
                <AnimatePresence>
                  {!isTransformed && chaosElements.map((element, index) => (
                    <motion.div
                      key={index}
                      className="absolute"
                      style={{
                        top: element.position.top,
                        left: element.position.left,
                        rotate: `${element.rotation}deg`,
                      }}
                      initial={{ opacity: 1, scale: 1 }}
                      exit={{ 
                        opacity: 0, 
                        scale: 0.8, 
                        x: Math.random() * 200 - 100,
                        y: Math.random() * 200 - 100,
                        rotate: element.rotation + (Math.random() * 180 - 90),
                      }}
                      transition={{ duration: 0.8, delay: index * 0.1 }}
                    >
                      {element.type === 'spreadsheet' && (
                        <div className="bg-white rounded-lg shadow-xl border border-slate-300 p-4 min-w-[200px]">
                          <div className="flex items-center space-x-2 mb-3">
                            <FileSpreadsheet className="w-4 h-4 text-green-600" />
                            <span className="text-xs font-medium text-slate-700 truncate">
                              {element.title}
                            </span>
                          </div>
                          <div className="space-y-2">
                            {Array.from({ length: 4 }).map((_, i) => (
                              <div key={i} className="flex space-x-1">
                                <div className="w-2 h-2 bg-slate-300 rounded"></div>
                                <div className="w-12 h-2 bg-slate-300 rounded"></div>
                                <div className="w-8 h-2 bg-red-300 rounded"></div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {element.type === 'calculator' && (
                        <div className="bg-slate-800 rounded-lg shadow-xl p-3 w-20">
                          <Calculator className="w-6 h-6 text-slate-300 mx-auto mb-2" />
                          <div className="grid grid-cols-3 gap-1">
                            {Array.from({ length: 9 }).map((_, i) => (
                              <div key={i} className="w-4 h-3 bg-slate-600 rounded-sm"></div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {element.type === 'sticky' && (
                        <div className="bg-yellow-200 rounded-lg shadow-lg p-3 w-24 border-l-4 border-yellow-400">
                          <StickyNote className="w-4 h-4 text-yellow-700 mb-1" />
                          <p className="text-xs font-medium text-yellow-800">
                            {element.text}
                          </p>
                        </div>
                      )}
                    </motion.div>
                  ))}
                </AnimatePresence>

                {/* Additional Chaos Elements */}
                <AnimatePresence>
                  {!isTransformed && (
                    <>
                      <motion.div
                        className="absolute top-16 left-8"
                        exit={{ opacity: 0, scale: 0, rotate: -180 }}
                        transition={{ duration: 0.6, delay: 0.4 }}
                      >
                        <Coffee className="w-8 h-8 text-amber-600" />
                        <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-amber-100 rounded-full border-2 border-amber-300"></div>
                      </motion.div>
                      
                      <motion.div
                        className="absolute bottom-20 right-20 flex items-center space-x-2 bg-red-100 px-3 py-2 rounded-full border border-red-200"
                        exit={{ opacity: 0, y: -50, scale: 0.5 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                      >
                        <AlertTriangle className="w-4 h-4 text-red-500" />
                        <span className="text-xs text-red-700 font-medium">Payment overdue!</span>
                      </motion.div>
                    </>
                  )}
                </AnimatePresence>

                {/* LotteryFlow Dashboard (After) */}
                <AnimatePresence>
                  {isTransformed && (
                    <motion.div
                      className="absolute inset-6"
                      initial={{ opacity: 0, scale: 0.9, y: 20 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.9, y: 20 }}
                      transition={{ duration: 0.8, delay: 0.5 }}
                    >
                      {/* Magic Sparkles Effect */}
                      {Array.from({ length: 12 }).map((_, i) => (
                        <motion.div
                          key={i}
                          className="absolute w-1 h-1 bg-gradient-to-r from-emerald-400 to-cyan-400 rounded-full"
                          style={{
                            left: `${Math.random() * 100}%`,
                            top: `${Math.random() * 100}%`,
                          }}
                          animate={{
                            scale: [0, 1, 0],
                            opacity: [0, 1, 0],
                            rotate: [0, 180, 360],
                          }}
                          transition={{
                            duration: 1.5,
                            delay: Math.random() * 0.5,
                            repeat: Infinity,
                            repeatDelay: 2,
                          }}
                        />
                      ))}

                      {/* Hero-Quality Dashboard */}
                      <div className="bg-white rounded-2xl shadow-2xl p-6 h-full border border-emerald-100 overflow-hidden">
                        
                        {/* Dashboard Header */}
                        <div className="flex items-center justify-between mb-6">
                          <div className="flex items-center space-x-3">
                            <div className="w-12 h-12 bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg">
                              <BarChart3 className="w-7 h-7 text-white" />
                            </div>
                            <div>
                              <h3 className="text-xl font-bold text-slate-800">LotteryFlow Dashboard</h3>
                              <p className="text-sm text-slate-500">Mike's Convenience Store</p>
                            </div>
                          </div>
                          
                          <motion.div
                            className="flex items-center space-x-2 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white px-6 py-3 rounded-full shadow-lg"
                            animate={{ scale: [1, 1.05, 1] }}
                            transition={{ duration: 2, repeat: Infinity }}
                          >
                            <CheckCircle className="w-5 h-5" />
                            <span className="font-semibold">On Track</span>
                          </motion.div>
                        </div>

                        {/* Compact Metrics Row */}
                        <div className="grid grid-cols-3 gap-4 mb-6">
                          {dashboardMetrics.map((metric, index) => {
                            const IconComponent = metric.icon;
                            return (
                              <motion.div
                                key={metric.label}
                                className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-3 border border-slate-200"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: 0.8 + index * 0.1 }}
                              >
                                <div className="flex items-center space-x-2 mb-1">
                                  <IconComponent className="w-4 h-4 text-emerald-600" />
                                  <div className="text-xs text-slate-600">{metric.label}</div>
                                </div>
                                <div className="text-lg font-bold text-slate-800">{metric.value}</div>
                                {metric.date && (
                                  <div className="text-xs text-slate-500">{metric.date}</div>
                                )}
                              </motion.div>
                            );
                          })}
                        </div>

                        {/* Enhanced Prominent Chart */}
                        <motion.div
                          className="bg-gradient-to-br from-emerald-50 via-cyan-50 to-blue-50 rounded-2xl p-6 border-2 border-emerald-200 shadow-inner"
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ duration: 0.8, delay: 1.2 }}
                        >
                          <div className="flex items-center justify-between mb-6">
                            <div>
                              <h4 className="text-lg font-bold text-slate-800 mb-1">Lottery Payment Forecast</h4>
                              <p className="text-sm text-slate-600">Next 6 months prediction</p>
                            </div>
                            <div className="flex items-center space-x-3">
                              <div className="flex items-center space-x-2 bg-emerald-100 px-3 py-1 rounded-full">
                                <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                                <span className="text-xs font-medium text-emerald-700">Actual</span>
                              </div>
                              <div className="flex items-center space-x-2 bg-cyan-100 px-3 py-1 rounded-full">
                                <div className="w-2 h-2 bg-cyan-500 rounded-full"></div>
                                <span className="text-xs font-medium text-cyan-700">Projected</span>
                              </div>
                              <div className="flex items-center space-x-2 text-emerald-600">
                                <TrendingUp className="w-4 h-4" />
                                <span className="text-sm font-bold">95.8%</span>
                              </div>
                            </div>
                          </div>
                          
                          {/* Professional Chart */}
                          <div className="relative">
                            {/* Chart Background Grid */}
                            <div className="absolute inset-0 grid grid-rows-4 opacity-20">
                              {Array.from({ length: 4 }).map((_, i) => (
                                <div key={i} className="border-b border-slate-300"></div>
                              ))}
                            </div>
                            
                            {/* Chart Bars */}
                            <div className="relative flex items-end justify-between h-32 px-2">
                              {chartData.map((data, i) => (
                                <motion.div
                                  key={data.month}
                                  className="flex flex-col items-center space-y-2"
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  transition={{ duration: 0.6, delay: 1.4 + i * 0.1 }}
                                >
                                  {/* Value Label */}
                                  <motion.div
                                    className="text-xs font-bold text-slate-700 bg-white px-2 py-1 rounded shadow-sm"
                                    initial={{ y: 10, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    transition={{ duration: 0.4, delay: 1.8 + i * 0.1 }}
                                  >
                                    ${(data.value / 1000).toFixed(1)}k
                                  </motion.div>
                                  
                                  {/* Bar - Actual or Projected */}
                                  <motion.div
                                    className={`w-12 rounded-t-lg shadow-lg ${
                                      data.actual 
                                        ? 'bg-gradient-to-t from-emerald-500 to-emerald-400' 
                                        : 'bg-gradient-to-t from-cyan-400 to-cyan-300'
                                    }`}
                                    initial={{ height: 0 }}
                                    animate={{ height: `${(data.value / 3500) * 100}%` }}
                                    transition={{ 
                                      duration: 1.2, 
                                      delay: 1.6 + i * 0.1,
                                      type: "spring",
                                      bounce: 0.3
                                    }}
                                  />
                                  
                                  {/* Month Label */}
                                  <div className="text-xs font-medium text-slate-600 mt-1">
                                    {data.month}
                                  </div>
                                </motion.div>
                              ))}
                            </div>
                            
                            {/* Dynamic Trend Line */}
                            <motion.svg
                              className="absolute inset-0 w-full h-32 pointer-events-none"
                              initial={{ pathLength: 0 }}
                              animate={{ pathLength: 1 }}
                              transition={{ duration: 3, delay: 2.5 }}
                            >
                              <motion.path
                                d={(() => {
                                  const width = 480; // approximate chart width
                                  const height = 128; // chart height
                                  const padding = 60;
                                  const stepX = (width - padding * 2) / (chartData.length - 1);
                                  
                                  let path = '';
                                  chartData.forEach((data, i) => {
                                    const x = padding + i * stepX;
                                    const y = height - ((data.value / 3500) * (height - 40)) - 20;
                                    
                                    if (i === 0) {
                                      path += `M ${x} ${y}`;
                                    } else {
                                      path += ` L ${x} ${y}`;
                                    }
                                  });
                                  
                                  return path;
                                })()}
                                stroke="url(#trendGradient)"
                                strokeWidth="3"
                                fill="none"
                                strokeDasharray="5,5"
                              />
                              <defs>
                                <linearGradient id="trendGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                  <stop offset="0%" stopColor="#10b981" />
                                  <stop offset="50%" stopColor="#06b6d4" />
                                  <stop offset="100%" stopColor="#3b82f6" />
                                </linearGradient>
                              </defs>
                            </motion.svg>
                          </div>
                          
                          {/* Chart Footer */}
                          <div className="mt-4 p-3 bg-white rounded-lg border border-emerald-200">
                            <div className="flex items-center justify-between text-sm">
                              <div className="flex items-center space-x-2">
                                <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                                <span className="text-slate-600">Predicted cash flow impact: </span>
                                <span className="font-bold text-emerald-700">+$2,847 buffer</span>
                              </div>
                              <div className="text-xs text-slate-500">
                                Last updated: {currentTime.toLocaleDateString()}
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Transformation Magic Effect */}
                <AnimatePresence>
                  {isTransformed && (
                    <motion.div
                      className="absolute inset-0 pointer-events-none"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                    >
                      <motion.div
                        className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
                        animate={{
                          scale: [0, 2, 0],
                          rotate: [0, 180, 360],
                        }}
                        transition={{ duration: 1, delay: 0.2 }}
                      >
                        <Sparkles className="w-16 h-16 text-emerald-400" />
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            {/* Coffee Cup & Ambient Details */}
            <motion.div
              className="absolute -bottom-6 -right-6"
              animate={{
                rotate: isTransformed ? [0, -5, 5, 0] : 0,
              }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <div className="w-12 h-12 bg-amber-100 rounded-full shadow-lg flex items-center justify-center border-4 border-white">
                <Coffee className="w-6 h-6 text-amber-600" />
              </div>
            </motion.div>
          </motion.div>

          {/* Simple CTA Section */}
          <motion.div
            className="mt-12 text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
          >
            <motion.button
              onClick={() => navigate('/auth/register')}
              className={getCTAClasses('primary')}
              whileHover={{ 
                scale: 1.05,
                boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
              }}
              whileTap={{ scale: 0.95 }}
            >
              <span className="flex items-center space-x-3">
                <span>{CTA_MESSAGES.transformation}</span>
                <motion.div
                  animate={{ x: [0, 5, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                >
                  <Zap className="w-5 h-5" />
                </motion.div>
              </span>
            </motion.button>

            <motion.p
              className="mt-4 text-slate-600 text-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
            >
              Free trial • No credit card required • 95.8% forecast accuracy
            </motion.p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};